import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import LoadingState from '@/components/LoadingState';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';
import { logLoginActivity } from '@/utils/activityLogger';
import { requestResellerMagicLinkResend } from '@/utils/magic-link/sendMagicLinkEmail';
import { otpTypeFromLink } from '@/utils/magic-link/otpType';
import { isMfaRequired } from '@/utils/featureFlags';

function parseAltEmails(raw: unknown): string[] {
  if (Array.isArray(raw)) return (raw as string[]).filter(Boolean);
  if (typeof raw === 'string' && raw) return [raw];
  return [];
}

type SmsMfaFactor = { id: string; phone_number: string | null };

async function fetchActiveSmsFactor(
  userId: string,
  email: string
): Promise<SmsMfaFactor | null> {
  const { data, error } = await supabase
    .from('user_mfa_factors')
    .select('id, phone_number')
    .eq('user_id', userId)
    .eq('secret', email)
    .eq('is_active', true)
    .eq('factor_type', 'sms')
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('[ResellerAuthCallback] Error checking MFA factor:', error);
  }

  return data;
}

function hasEnrolledSmsMfa(factor: SmsMfaFactor | null): boolean {
  return Boolean(factor?.phone_number?.trim());
}

const ResellerAuthCallback = () => {
  const [error, setError] = useState<string | null>(null);
  // Only a token GoTrue refused is an expired link. The session lookup, link
  // type and portal expiry below all run after sign-in and report themselves.
  const [linkRejected, setLinkRejected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const processAuthCallback = async () => {
    setLoading(true);
    setLinkRejected(false);

    // Marks the token step so its failures read as a link problem and
    // everything after it does not. supabase-js returns an error object, but a
    // transport failure rejects, and both mean the link did not get the caller in.
    const rejectingLink = async <T extends { error: unknown },>(
      step: () => Promise<T>,
    ): Promise<T> => {
      try {
        const result = await step();
        if (result?.error) setLinkRejected(true);
        return result;
      } catch (err) {
        setLinkRejected(true);
        throw err;
      }
    };

    try {
      // Check if there's a hash token in the URL
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      // Get token from query params for magic link flow
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const submissionIdentifier = urlParams.get('submission_identifier');
      
      // Store submission identifier in sessionStorage
      if (submissionIdentifier) {
        sessionStorage.setItem('figabl_reseller_submission_identifier', submissionIdentifier);
      }
      
      if (token) {
        // Replay the type the link carries. An address with no auth user gets a
        // signup token, and assuming 'magiclink' rejects a first use.
        const { error } = await rejectingLink(() => supabase.auth.verifyOtp({
          token_hash: token,
          type: otpTypeFromLink(urlParams.get('type'))
        }));

        if (error) throw error;

        // Get session after successful verification
        const { data: { session } } = await supabase.auth.getSession();

        // Log the magic link login activity
        if (session?.user?.email) {
          try {
            await logLoginActivity(
              session.user.email,
              'magic_link',
              session.user.id,
              submissionIdentifier || undefined
            );
          } catch (logError) {
            console.error('[ResellerAuthCallback] Failed to log login activity:', logError);
          }
        }

        // Fetch the magic link session to get organisation info
        const { data: sessionData, error: sessionError } = await supabase
          .from('magic_link_sessions')
          .select('email, organisation_id, link_type, expires_at, alternative_email')
          .eq('submission_identifier', submissionIdentifier)
          .maybeSingle();

        if (sessionError || !sessionData) {
          console.error('[ResellerAuthCallback] Session data not found:', sessionError);
          throw new Error(
            'We signed you in, but could not find the portal invitation for this link. Please ask your Figabl contact to send a new one.'
          );
        }

        // Verify this is a reseller portal link
        if (sessionData.link_type !== 'reseller_portal') {
          console.error('[ResellerAuthCallback] Invalid link type:', sessionData.link_type);
          throw new Error('Invalid link type for reseller portal');
        }

        // Verify portal access has not expired
        if (sessionData.expires_at && new Date(sessionData.expires_at) < new Date()) {
          console.error('[ResellerAuthCallback] Portal access has expired:', sessionData.expires_at);
          throw new Error('This portal link has expired. Please request a new one.');
        }

        if (!sessionData.organisation_id) {
          throw new Error('Organisation ID not found in session');
        }

        // Store reseller context for MFA verification
        const recipientEmail = urlParams.get('recipient_email');
        const altEmails = parseAltEmails(sessionData.alternative_email);

        const effectiveResellerEmail =
          recipientEmail &&
          sessionData.email !== recipientEmail &&
          altEmails.includes(recipientEmail)
            ? recipientEmail
            : session?.user?.email || sessionData.email;

        sessionStorage.setItem('figabl_reseller_org_id', sessionData.organisation_id);
        sessionStorage.setItem('figabl_reseller_email', effectiveResellerEmail);

        // Local Supabase skips both the alt-email and primary-email MFA gates.
        if (!isMfaRequired()) {
          navigate('/client/portal');
          return;
        }

        // Check if this link was sent to an alternative email recipient

        if (
          recipientEmail &&
          session?.user?.id &&
          sessionData.email !== recipientEmail &&
          altEmails.includes(recipientEmail)
        ) {
          try {
            const { data: existingFactor } = await supabase
              .from('user_mfa_factors')
              .select('id, phone_number')
              .eq('user_id', session.user.id)
              .eq('secret', recipientEmail)
              .eq('is_active', true)
              .maybeSingle();

            if (hasEnrolledSmsMfa(existingFactor)) {
              navigate('/verify-mfa', {
                state: {
                  email: recipientEmail,
                  isAltEmailRecipient: true,
                  isResellerPortalAccess: true,
                  primaryEmail: sessionData.email,
                  organisationId: sessionData.organisation_id,
                  from: { pathname: '/client/portal' }
                }
              });
            } else {
              navigate('/setup-mfa', {
                state: {
                  email: recipientEmail,
                  isAltEmailEnrollment: true,
                  isResellerPortalAccess: true,
                  primaryEmail: sessionData.email,
                  organisationId: sessionData.organisation_id,
                  submissionIdentifier,
                  from: { pathname: '/client/portal' }
                }
              });
            }
            return;
          } catch (altEmailErr) {
            console.error('[ResellerAuthCallback] Error handling alt-email recipient flow:', altEmailErr);
            // Fall through to primary email flow
          }
        }

        // Route to MFA setup (phone entry + enroll) or verification based on enrollment
        const primaryFactor =
          session?.user?.id != null
            ? await fetchActiveSmsFactor(session.user.id, sessionData.email)
            : null;

        if (hasEnrolledSmsMfa(primaryFactor)) {
          navigate('/verify-mfa', {
            state: {
              email: sessionData.email,
              isResellerPortalAccess: true,
              organisationId: sessionData.organisation_id,
              submissionIdentifier,
              from: { pathname: '/client/portal' }
            }
          });
        } else {
          navigate('/setup-mfa', {
            state: {
              email: sessionData.email,
              isResellerPortalAccess: true,
              organisationId: sessionData.organisation_id,
              submissionIdentifier,
              from: { pathname: '/client/portal' }
            }
          });
        }
        return;
        
      } else if (accessToken && refreshToken) {
        // Handle external oauth flow
        const { error } = await rejectingLink(() => supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        }));

        if (error) throw error;
      }

      // Fallback - shouldn't reach here normally
      setLoading(false);
      setError('Authentication flow incomplete');
      
    } catch (error) {
      console.error('[ResellerAuthCallback] Authentication error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred during authentication');
      setLoading(false);
    }
  };

  useEffect(() => {
    processAuthCallback();
  }, [navigate]);

  const handleRequestNewLink = async () => {
    try {
      setSendingLink(true);
      setLinkSent(false);

      const urlParams = new URLSearchParams(location.search);
      const submissionIdentifier = urlParams.get('submission_identifier') || '';
      const resendProof = urlParams.get('resend_proof') || '';

      const { success, error: sendError } = await requestResellerMagicLinkResend(submissionIdentifier, resendProof);
      if (!success) throw new Error(sendError || 'Failed to send magic link');

      setSendingLink(false);
      setLinkSent(true);

      toast({
        title: "Magic link sent",
        description: "Please check your email for the new login link."
      });
    } catch (err) {
      console.error('Error sending magic link:', err);
      setSendingLink(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to send magic link"
      });
    }
  };

  if (loading) {
    return <LoadingState message="Verifying your authentication..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <main className="flex-grow flex items-center justify-center p-4">
          <div className="bg-card p-6 rounded-lg shadow-md max-w-md w-full">
            <h2 className="text-destructive text-xl font-semibold mb-4">
              {linkRejected ? 'Magic Link Expired' : 'Portal access could not be opened'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {linkRejected
                ? 'This link is no longer valid. It may have already been used, or it may have expired. Request a new one below.'
                : error}
            </p>
            <Button
              onClick={handleRequestNewLink}
              className="w-full mb-4 flex items-center justify-center"
              disabled={sendingLink || linkSent}
            >
              <Mail className="mr-2 h-4 w-4" />
              {sendingLink
                ? 'Sending Magic Link...'
                : linkSent
                ? 'Magic Link Sent'
                : 'Resend Magic Link'}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return <LoadingState message="Redirecting..." />;
};

export default ResellerAuthCallback;
